import React, { useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { useParams } from "react-router-dom";
import { useReport } from "../hooks/useReporting";
import { ClientScopeProvider, useClientScope } from "../../../../../shared/contexts";
import { useClientBranding } from "../../../../../shared/hooks";
import { ReportPageView } from "../components/ReportPageView";
import { SlideFrame } from "../components/SlideFrame";
import { splitIntoPages } from "../utils/pages";
import { resolveBranding } from "../utils/branding";

/**
 * Print/export render of a report: one dark 16:9 slide per report page, no app
 * chrome. Headless Chromium loads this route for both PDF (page.pdf) and PPTX
 * (per-slide screenshots of [data-report-slide]) — see api/admin/reports/pdf.ts
 * and pptx.ts. Each slide sets window.__REPORT_READY__ once data has painted so
 * the exporter knows when to capture.
 *
 * Runs under its own top-level router entry so exports contain only report
 * content.
 */
const ReportPrintInner: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const { effectiveClientId } = useClientScope();
  const { data: report, isLoading } = useReport(reportId);
  // Render against the client the report was scoped to (fallback: live scope).
  const reportClientId = report?.client_id ?? effectiveClientId;
  const { data: brandingData } = useClientBranding(reportClientId);
  const branding = useMemo(
    () => resolveBranding(brandingData?.data),
    [brandingData],
  );

  const pages = useMemo(
    () => (report ? splitIntoPages(report.layout?.blocks ?? []) : []),
    [report],
  );

  useEffect(() => {
    if (isLoading || !report) return;
    // Allow charts/tables to finish painting before signalling readiness.
    const timer = setTimeout(() => {
      (window as unknown as { __REPORT_READY__?: boolean }).__REPORT_READY__ =
        true;
    }, 2500);
    return () => clearTimeout(timer);
  }, [isLoading, report]);

  if (!report) return null;

  return (
    <Box sx={{ bgcolor: "#0B0F14" }}>
      {pages.map((page, i) => (
        <Box
          key={i}
          data-report-slide
          sx={{
            // 16:9 slide canvas matching the deck.
            width: "1280px",
            height: "720px",
            bgcolor: "#0B0F14",
            color: "#fff",
            p: 6,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            pageBreakAfter: i < pages.length - 1 ? "always" : "auto",
            breakAfter: i < pages.length - 1 ? "page" : "auto",
          }}
        >
          <SlideFrame branding={branding}>
            <ReportPageView
              blocks={page}
              clientId={reportClientId}
              branding={branding}
              variant="print"
            />
          </SlideFrame>
        </Box>
      ))}
    </Box>
  );
};

export const ReportPrint: React.FC = () => (
  <ClientScopeProvider>
    <ReportPrintInner />
  </ClientScopeProvider>
);
